import AnalyticsProjectTagger from 'components/AnalyticsProjectTagger';
import Layout, { TopLevelMenuItem } from 'components/Layout';
import Loading from 'components/Loading';
import { HOUR_IN_MILLISECONDS } from 'config';
import pages from 'modules';
import { pathMatchesWithPathTemplate } from 'modules/makePage';
import Page from 'modules/Page';
import { useSnackbar } from 'notistack';
import { useProjectClientQuery } from 'providers/api';
import { useAuthorisation } from 'providers/authorisation';
import { IAuthorisationContext } from 'providers/authorisation/context';
import React, { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from 'react-router-dom';
import {
  CSSTransition,
  SwitchTransition
} from 'react-transition-group';
import { I18nProvider } from '@lingui/react';
import {
  i18n as customI18n, i18n, loadLocale, loadStoredLocale, storeLocale
} from 'i18n';
import useRoles from 'hooks/useRoles';
import { isLabOnlyUser, isPcUser } from 'utils/roles';
import DocusaurusPage from 'components/Layout/DocusaurusPage';
import TermsAndConditionsGate from 'components/TermsAndConditionsGate';
import ProjectsRootRedirect from 'components/ProjectsRootRedirect';
import Box from '@mui/material/Box';
import ScenarioBanner, { SCENARIO_BANNER_HEIGHT, isScenarioBannerHiddenForPath } from 'components/ScenarioBanner/ScenarioBanner';
import { ScenarioProvider, useScenario } from 'modules/project/providers/scenario';
import EnvironmentBanner, { ENVIRONMENT_BANNER_HEIGHT, getEnvironment } from './components/EnvironmentBanner/EnvironmentBanner';

// Wraps the app chrome in a viewport-height-minus-banners box. The CSS transform
// establishes a containing block so every position:fixed element (top bar, side
// menu, 3D canvas, side drawers, scene toolbars) is bounded above the fixed
// bottom banners instead of being covered by them.
const AppContentFrame = ({ children }: { children: React.ReactNode }) => {
  const { activeScenarioId } = useScenario();
  const { pathname } = useLocation();

  const scenarioBannerVisible = !!activeScenarioId && !isScenarioBannerHiddenForPath(pathname);
  const bottomBannerHeight = (getEnvironment() ? ENVIRONMENT_BANNER_HEIGHT : 0) + (scenarioBannerVisible ? SCENARIO_BANNER_HEIGHT : 0);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: `calc(100vh - ${bottomBannerHeight}px)`,
        overflowX: 'hidden',
        overflowY: 'auto',
        transform: 'translateZ(0)',
      }}
    >
      {children}
    </Box>
  );
};

interface RedirectWithErrorProps {
  error: string,
  to: string,
}
const RedirectWithError = ({ error, to }: RedirectWithErrorProps) => {
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    enqueueSnackbar(error, { variant: 'error' });
  }, []);

  return <Navigate to={to} replace />;
};

interface AuthorisePageProps {
  page: Page<any>,
  authorisationContext: IAuthorisationContext
}

const AuthorisePage = ({ page, authorisationContext }: AuthorisePageProps) => {
  const params = useParams();
  const location = useLocation();
  const roles = useRoles();
  const isPatersonAndCooke = isPcUser(roles);
  const authoriseConfig = page.authorise ? page.authorise(authorisationContext, params as any) : undefined;
  const { data, isFetching, error } = useProjectClientQuery(authoriseConfig?.teamId ?? '', {
    enabled: !!authoriseConfig?.teamId,
    staleTime: 24 * HOUR_IN_MILLISECONDS,
  });

  if (authorisationContext.state.loading || (!!authoriseConfig?.teamId && isFetching)) {
    return <Loading />;
  }

  const ids = authoriseConfig?.teamId && data ? [authoriseConfig?.teamId, data] : [];

  const isAuthorised = authoriseConfig ? authoriseConfig.handler(ids) : true;
  // Allow admin pages for administrators, but also allow P&C users to access project admin pages
  const isPcProjectAdminPage = location.pathname === '/admin/projects/new' || location.pathname === '/admin/projects';
  const isAdminAuthorised = page.admin
    ? (authorisationContext.functions.isAdmin() || (isPatersonAndCooke && isPcProjectAdminPage))
    : true;

  if (!isAuthorised || !isAdminAuthorised || !!error) {
    return <RedirectWithError to="/" error={`Not authorised to view ${page.title}`} />;
  }

  return (<page.component />);
};

const App = () => {
  const location = useLocation();
  const authorisationContext = useAuthorisation();
  const roles = useRoles();
  const isLabOnly = isLabOnlyUser(roles) && !authorisationContext.state.user?.permissions?.teamRoles?.length;
  const userId = authorisationContext.state.user.id;
  const [locale, setLocale] = useState(() => loadStoredLocale(userId));
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render

  useEffect(() => {
    loadLocale(locale); // Load new translations
    setForceUpdate((prev) => prev + 1); // Trigger a re-render
  }, [locale]);

  const toggleLocale = (newLocale: string) => {
    if (newLocale !== locale) {
      setLocale(newLocale);
      storeLocale(userId, newLocale);
    }
  };

  const matchingRouteForCurrentLevel = pages.find((p) => pathMatchesWithPathTemplate(location.pathname, p.routeProps.path ?? ''));
  const routeKey = matchingRouteForCurrentLevel?.routeProps.path;

  // Scope the active scenario to a single project: a new project remounts the provider
  // (resetting to master), while navigating between a project's stope/pour routes keeps it.
  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
  const scenarioProjectId = projectMatch ? projectMatch[1] : null;
  const scenarioScopeKey = scenarioProjectId ?? 'no-project';

  const menu: TopLevelMenuItem[] = pages
    .filter((page) => {
      if (!page.mainMenuConfig) return false;
      if (page.authorise) {
        throw new Error(
          i18n._('"authorise" page configuration cannot currently be used for a top level page.'),
        );
      }
      // admin-only check - allow P&C users to access project admin pages
      const isPcProjectAdminPage = page.routeProps.path === '/admin/projects/new' || page.routeProps.path === '/admin/projects';
      if (page.admin && !authorisationContext.functions.isAdmin() && !(isPcUser(roles) && isPcProjectAdminPage)) return false;
      // lab-only restriction: keep only QAQCModule pages
      if (isLabOnly && !(page.routeProps.path ?? '').toLowerCase().startsWith('/qaqc')) {
        return false;
      }
      return true;
    })
    .map(({ mainMenuConfig, title, routeProps, navigationPath }) => ({
      group: mainMenuConfig?.group,
      display: i18n._(title),
      path: (navigationPath || routeProps.path) as string,
      icon: mainMenuConfig!.iconComponent,
    }));

  return (
    <I18nProvider i18n={customI18n}>
      <EnvironmentBanner />
      <AnalyticsProjectTagger projectId={scenarioProjectId} />
      <TermsAndConditionsGate>
        <ScenarioProvider key={scenarioScopeKey} projectId={scenarioProjectId}>
          <ScenarioBanner />
          <AppContentFrame>
            <Layout
              key={forceUpdate}
              menu={menu}
              toggleLocale={toggleLocale}
              locale={locale}
              menuOpen={!isLabOnly}
            >
              <SwitchTransition>
                <CSSTransition key={routeKey} timeout={200} classNames="fade" in>
                  <Routes location={location} key={routeKey}>
                    {pages.map((page) => (
                      <Route
                        key={page.title}
                        {...page.routeProps}
                        element={(
                          <AuthorisePage
                            page={page}
                            authorisationContext={authorisationContext}
                          />
                        )}
                      />
                    ))}
                    <Route path="/documentation/*" Component={DocusaurusPage} />
                    <Route path="*" Component={ProjectsRootRedirect} />
                  </Routes>
                </CSSTransition>
              </SwitchTransition>
            </Layout>
          </AppContentFrame>
        </ScenarioProvider>
      </TermsAndConditionsGate>
    </I18nProvider>
  );
};

export default App;
